"use client";

import { useRef } from "react";
import Link from "next/link";
import { gsap, useGSAP } from "@/lib/gsap";
import { useNearViewport } from "@/hooks/useNearViewport";
import { AlertTriangle, ServerCrash, Wallet, Network, Check, ArrowRight } from "lucide-react";

/**
 * Pain → Promise comparison section.
 *
 * Design strategy: the "Without" side is visually *dull and faded* (muted grays,
 * dimmed icons, contained in a subtle card). The "With" side is *bright
 * and open* (brand green/cyan, no card wrapper, ambient glow). Contrast comes from
 * energy level, not from introducing off-brand colors like red.
 *
 * GSAP upgrade: Pain items stagger in with a "piling on" wobble, then the divider
 * draws itself, and the promise side sweeps in with confident back.out ease.
 */

const PAINS = [
  { icon: ServerCrash,   label: "6–8 weeks of DevOps before your first prod RPC call" },
  { icon: Wallet,        label: "Hardware spend that spikes 3–5× under load" },
  { icon: AlertTriangle, label: "One config error → full chain outage, no rollback" },
  { icon: Network,       label: "Five chains. Five vendors. Five on-call rotations." },
] as const;

const PROMISES = [
  "One API, every chain you ship to",
  "Usage-based pricing, no hardware capex",
  "Auto-failover in under 500ms, zero-downtime upgrades",
  "Single dashboard. One on-call. 24/7 monitored.",
] as const;

export default function PainPromise() {
  const sectionRef = useRef<HTMLElement>(null);
  const isNear = useNearViewport(sectionRef);

  useGSAP(
    () => {
      if (!sectionRef.current || !isNear) return;

      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        // ── Pain side: items pile on with instability feel ─────────────
        const painTl = gsap.timeline({
          scrollTrigger: {
            trigger: ".pain-card",
            start: "top 78%",
            toggleActions: "play none none none",
          },
        });

        painTl
          .from(".pain-heading", {
            autoAlpha: 0,
            y: 30,
            duration: 0.6,
          })
          .from(".pain-item", {
            autoAlpha: 0,
            y: 28,
            x: -8,
            rotation: -0.5,
            stagger: 0.12,
            duration: 0.5,
            ease: "power2.out",
          }, "-=0.2")
          // Icon shake after items are visible
          .to(".pain-icon", {
            keyframes: [
              { x: -2, rotation: -2, duration: 0.08 },
              { x: 2, rotation: 1.5, duration: 0.08 },
              { x: -1.5, rotation: -1, duration: 0.08 },
              { x: 1, rotation: 0.5, duration: 0.08 },
              { x: 0, rotation: 0, duration: 0.1 },
            ],
            stagger: 0.1,
          });

        // ── Divider: draws itself ───────────────────────────────────────
        // Desktop vertical line
        gsap.from(".divider-line-v", {
          scaleY: 0,
          transformOrigin: "top center",
          duration: 0.8,
          ease: "power3.inOut",
          scrollTrigger: {
            trigger: ".divider-center",
            start: "top 70%",
            toggleActions: "play none none none",
          },
        });

        // Divider arrow pops
        gsap.from(".divider-arrow", {
          scale: 0,
          autoAlpha: 0,
          duration: 0.4,
          ease: "back.out(2)",
          scrollTrigger: {
            trigger: ".divider-center",
            start: "top 65%",
            toggleActions: "play none none none",
          },
        });

        // ── Promise side: confident entrance ─────────────────────────
        const promiseTl = gsap.timeline({
          scrollTrigger: {
            trigger: ".promise-col",
            start: "top 75%",
            toggleActions: "play none none none",
          },
        });

        promiseTl
          .from(".promise-heading", {
            autoAlpha: 0,
            y: 30,
            duration: 0.6,
          })
          .from(".promise-item", {
            autoAlpha: 0,
            y: 20,
            stagger: 0.1,
            duration: 0.5,
            ease: "back.out(1.2)",
          }, "-=0.2");

        // ── Background glow shift: scroll-linked ────────────────────
        gsap.fromTo(
          ".promise-glow",
          { autoAlpha: 0 },
          {
            autoAlpha: 1,
            duration: 1,
            scrollTrigger: {
              trigger: ".promise-col",
              start: "top 80%",
              end: "bottom 50%",
              scrub: 1,
            },
          },
        );

        // ── Parallax: glow drifts as you scroll ─────────────────────
        gsap.to(".promise-glow", {
          y: -40,
          x: 20,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });

        // Pain card subtle parallax (moves slower than content)
        gsap.to(".pain-card", {
          y: -15,
          ease: "none",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });
      });

      // Reduced motion fallback
      mm.add("(prefers-reduced-motion: reduce)", () => {
        gsap.set(
          ".pain-heading, .pain-item, .promise-heading, .promise-item, .divider-line-v, .divider-arrow, .promise-glow",
          { autoAlpha: 1, y: 0, x: 0, scale: 1, rotation: 0 },
        );
      });
    },
    { scope: sectionRef, dependencies: [isNear] },
  );

  return (
    <section
      ref={sectionRef}
      className="section-pad relative overflow-hidden bg-bg-base"
      aria-label="Why teams move to Instanodes"
    >
      {/* Ambient glow — teal/cyan on the promise side (replaces pure purple) */}
      <div
        aria-hidden
        className="promise-glow pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 50% 60% at 75% 50%, color-mix(in oklab, var(--accent) 8%, transparent) 0%, transparent 65%), radial-gradient(ellipse 40% 50% at 80% 40%, color-mix(in oklab, var(--good) 8%, transparent) 0%, transparent 55%)",
          visibility: "hidden",
        }}
      />

      <div className="mx-auto grid w-full max-w-7xl items-stretch gap-8 px-4 sm:px-6 lg:grid-cols-[1fr_auto_1fr] lg:gap-0 lg:px-8">

          {/* ── Left: Without Instanodes (faded, contained) ──────────── */}
          <div
            className="pain-card relative overflow-hidden rounded-2xl border border-border-subtle bg-bg-surface p-8 sm:p-10"
          >
            {/* Faded diagonal stripes watermark */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 -z-0 opacity-[0.03]"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(135deg, transparent, transparent 10px, rgba(255,255,255,0.5) 10px, rgba(255,255,255,0.5) 11px)",
              }}
            />

            <div className="relative">
              <p className="pain-heading mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-text-muted">
                Before Instanodes
              </p>
              <h2 className="pain-heading mb-8 text-2xl font-bold leading-[1.15] tracking-tight text-text-primary sm:text-3xl">
                Running blockchain infra is{" "}
                <span className="text-text-muted">a full-time tax on your team.</span>
              </h2>
              <ul className="space-y-4">
                {PAINS.map((p) => (
                  <li key={p.label} className="pain-item flex items-start gap-3">
                    <span className="pain-icon mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border-subtle bg-bg-elevated">
                      <p.icon size={14} className="text-text-muted" aria-hidden />
                    </span>
                    <span className="text-base leading-relaxed text-text-muted">{p.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ── Center: Divider ──────────────────────────────────────── */}
          <div className="divider-center flex items-center justify-center lg:px-6">
            {/* Mobile: horizontal line */}
            <div className="flex w-full items-center gap-3 lg:hidden">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border-medium to-transparent" />
              <span className="shrink-0 text-xs font-semibold uppercase tracking-widest text-text-muted">then</span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border-medium to-transparent" />
            </div>
            {/* Desktop: vertical gradient line */}
            <div className="hidden h-full lg:flex lg:flex-col lg:items-center lg:gap-3">
              <div
                className="divider-line-v w-px flex-1"
                style={{ background: "linear-gradient(to bottom, transparent, color-mix(in oklab, var(--accent) 28%, transparent) 30%, color-mix(in oklab, var(--good) 28%, transparent) 70%, transparent)" }}
              />
              <div
                className="divider-arrow relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-brand-cyan/30 bg-bg-elevated"
              >
                {/* Pulsing glow ring */}
                <span
                  aria-hidden
                  className="absolute inset-[-4px] rounded-full"
                  style={{
                    background: "radial-gradient(circle, color-mix(in oklab, var(--accent) 18%, transparent) 0%, transparent 70%)",
                    animation: "cta-pulse-ring 2s ease-in-out infinite",
                  }}
                />
                <span className="relative text-sm font-semibold text-brand-cyan">→</span>
              </div>
              <div
                className="divider-line-v w-px flex-1"
                style={{ background: "linear-gradient(to bottom, transparent, color-mix(in oklab, var(--good) 28%, transparent) 30%, transparent)" }}
              />
            </div>
          </div>

          {/* ── Right: With Instanodes (bright, open, alive) ─────────── */}
          <div
            className="promise-col relative p-8 sm:p-10"
          >
            {/* Soft dual-tone glow behind this column */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 -z-10 rounded-2xl"
              style={{
                background:
                  "radial-gradient(ellipse 80% 70% at 50% 50%, color-mix(in oklab, var(--accent) 7%, transparent) 0%, transparent 60%), radial-gradient(ellipse 60% 60% at 60% 60%, color-mix(in oklab, var(--good) 7%, transparent) 0%, transparent 55%)",
              }}
            />

            <p className="promise-heading mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-brand-cyan">
              After Instanodes
            </p>
            <h2 className="promise-heading mb-8 text-2xl font-bold leading-[1.15] tracking-tight text-text-primary sm:text-3xl">
              Boring infra,{" "}
              <span className="gradient-text-cyan">on tap.</span>
            </h2>
            <ul className="space-y-4">
              {PROMISES.map((promise) => (
                <li key={promise} className="promise-item flex items-start gap-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-brand-teal/20 bg-brand-teal/[0.08]">
                    <Check size={14} className="text-brand-cyan" aria-hidden />
                  </span>
                  <span className="text-base leading-relaxed text-text-secondary">{promise}</span>
                </li>
              ))}
            </ul>
            <Link
              href="/why-we-lead"
              className="promise-item group mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-cyan transition-colors hover:text-accent"
            >
              See how it works
              <ArrowRight size={14} aria-hidden className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

      </div>
    </section>
  );
}
